
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

export const useDashboard = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [userData, setUserData] = useState<any>(null);
  const [userStatus, setUserStatus] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isAuthCheckComplete, setIsAuthCheckComplete] = useState(false);

  // Make checkAuth a useCallback so we can safely use it in dependencies
  const checkAuth = useCallback(async () => {
    setIsLoading(true);
    
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      
      // User is not authenticated - this isn't necessarily an error for nav components
      if (!sessionData.session) {
        setUserData(null);
        setUserStatus(null);
        setIsAdmin(false);
        setIsAuthCheckComplete(true);
        setIsLoading(false);
        return;
      }
      
      const user = sessionData.session.user;
      
      // Get user status from client_onboarding
      const { data: onboardingData, error } = await supabase
        .from('client_onboarding')
        .select('id, status, full_name, company_name, role, phone')
        .eq('email', user.email)
        .maybeSingle();
      
      if (error) throw error;
      
      if (onboardingData) {
        console.log('Retrieved user status:', onboardingData.status);
        
        // Handle status value normalization
        let normalizedStatus = onboardingData.status;
        
        // Map status values if needed
        if (normalizedStatus === 'verified') {
          console.log('Mapping "verified" status to "approved"');
          normalizedStatus = 'approved';
        } else if (normalizedStatus === 'pending_verification') {
          console.log('Mapping "pending_verification" status to "pending"');
          normalizedStatus = 'pending';
        }
        
        setUserStatus(normalizedStatus);
        setIsAdmin(onboardingData.role === 'admin');
        
        // Update user data with additional info including the ID
        setUserData({
          ...user,
          id: onboardingData.id, // Make sure to include the onboarding record ID
          full_name: onboardingData.full_name,
          company_name: onboardingData.company_name,
          role: onboardingData.role,
          phone: onboardingData.phone,
          status: normalizedStatus
        });
      } else {
        console.log('No onboarding data found for user');
        setUserData(user);
        setUserStatus('pending'); // Default status
      }
      
      setIsAuthCheckComplete(true);
    } catch (error) {
      console.error('Error checking auth:', error);
      // Don't show toast errors for nav components - they're not critical
      setUserData(null);
      setIsAdmin(false);
    } finally {
      setIsLoading(false);
      setIsAuthCheckComplete(true);
    }
  }, []);

  useEffect(() => {
    // Initial auth check
    checkAuth();
    
    // Set up auth state listener
    const { data: authListener } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('Auth state changed:', event);
      if (event === 'SIGNED_OUT') {
        setUserData(null);
        setUserStatus(null);
        setIsAdmin(false);
      } else if (session && (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED')) {
        checkAuth();
      }
    });

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, [checkAuth]);

  // Separate useEffect for the subscription to prevent dependency issues
  useEffect(() => {
    if (!userData?.email) return;
    
    console.log('Setting up status subscription for:', userData.email);
    
    // Create a subscription to user status changes - this is crucial for real-time updates
    const userStatusSubscription = supabase
      .channel('client_onboarding_changes')
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'client_onboarding',
        filter: `email=eq.${userData.email}`
      }, (payload) => {
        console.log('Status update detected:', payload);
        
        // Update status immediately when changed in database
        if (payload.new && payload.new.status) {
          let newStatus = payload.new.status;
          
          // Map status values if needed
          if (newStatus === 'verified') {
            console.log('Mapping "verified" status to "approved"');
            newStatus = 'approved';
          } else if (newStatus === 'pending_verification') {
            console.log('Mapping "pending_verification" status to "pending"');
            newStatus = 'pending';
          }
          
          console.log('Setting new status:', newStatus);
          setUserStatus(newStatus);
          
          // Refresh full user data
          checkAuth();
          
          // Show toast notification
          toast({
            title: 'Account Status Updated',
            description: `Your account status has been updated to ${newStatus}`,
          });
        }
      })
      .subscribe();
    
    return () => {
      supabase.removeChannel(userStatusSubscription);
    };
  }, [userData?.email, checkAuth, toast]);

  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
      navigate('/auth');
      toast({
        title: 'Signed out',
        description: 'You have been signed out successfully',
      });
    } catch (error) {
      console.error('Sign out error:', error);
      toast({
        title: 'Error',
        description: 'There was a problem signing out',
        variant: 'destructive',
      });
    }
  };

  return {
    isLoading,
    userData,
    userStatus,
    isAdmin,
    isProfileOpen,
    setIsProfileOpen,
    handleSignOut,
    isAuthCheckComplete,
    refreshUserStatus: checkAuth
  };
};
